import { css, keyframes } from 'styled-components'
import { COLORS } from 'consts/colors'
import { MOBILE_SIZE_BREAKPOINT } from 'consts'
import { BOX_SHADOWS } from './consts'

export type TBoxShadowTypes =
  | 'previewAreaShadow'
  | 'shadow1'
  | 'shadow2'
  | 'mobileExpandShadow'
  | 'darkShadow'
  | 'headerShadow'
  | 'timelineShadow'

/**
 * Common scrollbar styles shared between vertical and horizontal scrollbars
 */
const commonScrollbarStyles = css`
  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: ${COLORS.secondaryLight};

    @media (max-width: ${MOBILE_SIZE_BREAKPOINT}px) {
      outline: none;
    }
  }
`

/**
 * Customizes the vertical scrollbar appearance
 */
export const customizeVerticalScrollbarThumbMixin = (width: number = 3) => {
  return css`
    &::-webkit-scrollbar {
      width: ${width}px;

      @media (max-width: ${MOBILE_SIZE_BREAKPOINT}px) {
        width: 3px;
      }
    }
    ${commonScrollbarStyles}
  `
}

/**
 * Customizes the horizontal scrollbar appearance
 */
export const customizeHorizontalScrollbarThumbMixin = (width: number = 3) => {
  return css`
    &::-webkit-scrollbar {
      height: ${width}px;
    }
    ${commonScrollbarStyles}
  `
}

export const disableImageDraggableStyleMixin = () => css`
  user-drag: none;
  user-select: none;
`

const setBorderMixinStyles = ({
  size,
  style,
  borderRadius,
  color,
  background,
  extraPixels = 0
}: {
  size: number
  style: string
  borderRadius: string
  color?: string
  background?: string | undefined
  extraPixels?: number
}) => {
  return css`
    content: '';
    position: absolute;
    width: calc(100% - ${size + extraPixels}px);
    height: calc(100% - ${size + extraPixels}px);
    background: ${background || 'transparent'};
    border: ${size}px ${style} ${color || COLORS.borderColor};
    border-radius: ${borderRadius};
    left: 0;
    top: 0;
    pointer-events: none;
  `
}

export const setBorderMixinAfter = ({
  size = 1,
  style = 'solid',
  borderRadius = '0',
  color,
  extraPixels = 0,
  background,
  zIndex
}: {
  size?: number
  style?: string
  borderRadius?: string
  color?: string
  extraPixels?: number
  background?: string | undefined
  zIndex?: number
}) => {
  return css`
    &::after {
      ${setBorderMixinStyles({
        size,
        style,
        borderRadius,
        color,
        extraPixels,
        background
      })}
      z-index: ${zIndex ? zIndex : 'unset'};
    }
  `
}

export const hideScrollbarMixin = () => {
  return css`
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  `
}

export const flexCenterMixin = (): string => {
  return `
  display:flex;
  justify-content:center;
  align-items:center;
  `
}

export const absoluteElementJustifyCenterMixin = (): string => {
  return `
      left: 0;
      right: 0;
      margin-right: auto;
      margin-left: auto;
    `
}

export const absoluteElementAlignCenterMixin = () => {
  return css`
    position: absolute;
    top: 0;
    bottom: 0;
    margin-top: auto;
    margin-bottom: auto;
  `
}

export const absoluteElementToCenterMixin = (): string => {
  return `
    ${absoluteElementAlignCenterMixin()}
    ${absoluteElementJustifyCenterMixin()}
    `
}

export const boxShadowStyles = (type: TBoxShadowTypes) => BOX_SHADOWS[type]
export const disableSomeContentStyles = (opacity = 0.4) => {
  return css`
    pointer-events: none;
    opacity: ${opacity} !important;
  `
}

export const setInputCommonStyles = (
  $isError: boolean | undefined,
  $isWarning: boolean | undefined,
  $isFocusing: boolean | undefined,
  inputBorder: string,
  secondarySemiLight: string,
  primaryDark: string,
  primaryLight: string,
  white: string
) => {
  return css`
    ${() => {
      if ($isError) {
        return css`
          border: 1px solid ${COLORS.accentRed};
        `
      }

      if ($isWarning) {
        return css`
          border: 1px solid ${COLORS.warningColor};
        `
      }

      return css`
        border: 1px solid ${inputBorder};
        &:hover {
          border: 1px solid ${secondarySemiLight};
          box-shadow: 0 0 0 1px #dfeaff; /* Outer border */
        }

        &:focus {
          border: 1px solid ${primaryDark};
        }
      `
    }}

    ${() => {
      if ($isFocusing) {
        const focusAnimation = keyframes`
        from {
          background-color: ${primaryLight};
        }
        to {
          background-color: ${white};
        }
      `

        return css`
          animation: ${focusAnimation} 3s forwards;
        `
      }

      return ''
    }}
  `
}
/**
 * Sets the border styles for an input element based on its error, warning, and focus state.
 * @param isError - Whether the input has an error.
 * @param isWarning - Whether the input has a warning.
 * @param isFocusing - Whether the input is being focused manually.
 */
export const setCommonBorderStyles = (
  isError?: boolean,
  isWarning?: boolean,
  isFocusing?: boolean
) => {
  if (isError) {
    return css`
      border: 1px solid ${COLORS.accentRed};
    `
  }

  if (isWarning) {
    return css`
      border: 1px solid ${COLORS.warningColor};
    `
  }

  if (isFocusing) {
    return css`
      border: 1px solid ${COLORS.primaryDark};
    `
  }

  return css`
    border: 1px solid ${COLORS.borderColor};

    &:hover {
      border: 1px solid ${COLORS.secondarySemiLight};
      box-shadow: 0 0 0 1px #dfeaff;
    }

    &:focus {
      border: 1px solid ${COLORS.primaryDark};
    }
  `
}

/**
 * Disables some content styles
 */

export const textUnderlineAnimationStyles = () => {
  return css`
    content: '';
    position: absolute;
    width: 100%;
    transform: scaleX(0);
    height: 1px;
    bottom: 0;
    left: 0;
    top: 100%;
    background-color: ${COLORS.primaryDark};
    transform-origin: center;
    transition: transform 0.25s ease-out;
  `
}

export const setBorderMixinBefore = ({
  size = 1,
  style = 'solid',
  borderRadius = '0',
  color,
  background,
  left = -1,
  right = -1,
  top = 0,
  bottom = 0,
  zIndex
}: {
  size?: number
  style?: string
  borderRadius?: string
  color?: string
  background?: string | undefined
  left?: number
  right?: number
  top?: number
  bottom?: number
  zIndex?: number
}) => {
  return css`
    &::before {
      content: '';
      position: absolute;
      background: ${background || 'transparent'};
      border: ${`${size}px  ${style} ${color || COLORS.borderColor}`};
      border-radius: ${borderRadius};
      pointer-events: none;

      left: ${left}px;
      right: ${right}px;
      top: ${top}px;
      bottom: ${bottom}px;
      z-index: ${zIndex ? zIndex : 'unset'};
    }
  `
}

export const iconButtonStyles = (disabled?: boolean) => {
  return css`
    width: 32px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    border-radius: 4px;
    transition: 0.3s;
    opacity: ${disabled ? 0.5 : 1};
    pointer-events: ${disabled ? 'none' : 'auto'};

    & > svg {
      fill: ${COLORS.secondarySemiDark};
    }

    &:hover {
      background: ${disabled ? 'none' : COLORS.primaryLight};

      & > svg {
        fill: ${disabled ? COLORS.secondarySemiDark : COLORS.secondaryDark};
      }
    }
  `
}
